<template>
  <component
    :is="linkComponent"
    v-if="linkComponent !== 'NuxtLink'"
    :to="spaLink ? to : null"
    :href="(element === 'a') || !spaLink && !isButton && !element ? to : null"
    :target="target ? target : null"
    class="VisionLink"
    :class="[
      {
        'VisionLink--line-link': lineLink,
        'VisionLink--primary': primary,
        'VisionLink--secondary': secondary,
        'VisionLink--promo-theme': hasPromoTheme,
        'VisionLink--mono-font': monoFont,
      },
      `VisionLink--text-transform-${textTransform}`,
      `VisionLink--theme-${theme}`,
      `VisionLink--display-${display}`,
    ]"
    @click="$emit('click', $event)"
  >
    <slot />
  </component>
  <NuxtLink
    v-else
    :to="to"
    :target="target ? target : null"
    class="VisionLink"
    :class="[
      {
        'VisionLink--line-link': lineLink,
        'VisionLink--primary': primary,
        'VisionLink--secondary': secondary,
        'VisionLink--promo-theme': hasPromoTheme,
        'VisionLink--mono-font': monoFont,
      },
      `VisionLink--text-transform-${textTransform}`,
      `VisionLink--theme-${theme}`,
      `VisionLink--display-${display}`,
    ]"
    @click="$emit('click', $event)"
  >
    <slot />
  </NuxtLink>
</template>

<script>
export default {
  name: 'VisionLink',

  props: {
    to: {
      type: [String, Object],
      required: false,
      default: '',
    },

    lineLink: {
      type: Boolean,
      default: false,
    },

    theme: {
      type: String,
      default: 'default',
      validator(value) {
        return [
          'default',
          'gradient',
          'green',
          'gold',
          'white',
          'red',
          'grey',
        ].includes(value)
      },
    },

    primary: {
      type: Boolean,
      default: false,
    },

    secondary: {
      type: Boolean,
      default: false,
    },

    monoFont: {
      type: Boolean,
      default: false,
    },

    textTransform: {
      type: String,
      default: 'normal',
      validator(value) {
        return ['normal', 'uppercase', 'lowercase', 'capitalize'].includes(
          value,
        )
      },
    },

    isButton: {
      type: Boolean,
      default: false,
    },

    element: { type: String, default: '', required: false },

    spaLink: {
      type: Boolean,
      default: false,
      required: false,
    },

    display: {
      type: String,
      default: 'flex',
      validator(value) {
        return ['flex', 'inline-block', 'block'].includes(value)
      },
    },

    target: {
      type: String,
      default: '',
    },

    hasPromoTheme: {
      type: Boolean,
      default: false
    }
  },

  emits: ['click'],

  computed: {
    linkComponent() {
      if (this.element) return this.element
      else if (this.isButton) return 'button'
      return this.spaLink ? 'NuxtLink' : 'a'
    },
  },
}
</script>

<style lang="postcss">
.VisionLink {
  --vision-link-text-colour: var(--vision-link-text-colour, theme('colors.off-black'));
  --vision-link-text-colour--hover: var(--vision-link-text-colour, theme('colors.off-black'));

  @apply flex transition;

  color: var(--vision-link-text-colour);

  &:hover {
    color: var(--vision-link-text-colour--hover);
  }
}

/* Line Link */

.VisionLink--line-link {
  @apply relative
    px-xxxs
    -mx-xxxs;

  &::before,
  &::after {
    @apply content-[''] absolute bottom-0
    h-px;

    background-color: var(--vision-link-text-colour);
    left: theme('spacing.xxxs');
    width: calc(100% - (theme('spacing.xxxs') * 2));
  }

  &::before {
    @apply scale-100 origin-left
      transition-transform duration-500;
  }

  &::after {
    @apply opacity-10;
  }

  &:hover {
    color: var(--vision-link-text-colour--hover);

    &::before {
      @apply scale-0 origin-right;
    }
  }

  &:not(.VisionLink--mono-font) {
    &.VisionLink--text-transform-normal {
      @apply normal-case;
    }

    &.VisionLink--text-transform-uppercase {
      @apply uppercase;
    }

    &.VisionLink--text-transform-lowercase {
      @apply lowercase;
    }

    &.VisionLink--text-transform-capitalize {
      @apply capitalize;
    }
  }

}

/* Primary Line Link */

.VisionLink--primary {
  &::after {
    @apply opacity-30 transition-opacity duration-300 ease-in-out;
  }

  &:hover,
  &:hover::after {
    @apply opacity-100;
  }
}

/* Secondary Line Link */

.VisionLink--secondary {
  &::after {
    @apply t-gradient
      opacity-50 transition-opacity duration-300 ease-in-out;
    background-clip: initial;
    -webkit-background-clip: initial;
  }

  &:hover::after {
    @apply opacity-100;
  }
}

/* Themes */
.VisionLink {
  &--theme-gradient {
    @apply t-gradient;

    &::after,
    &::before {
      @apply bg-gradient;
    }
  }

  &--theme-gold {
    &::before,
    &::after {
      background-image: var(--gradient-gold);
    }

    @apply t-gradient-gold;
  }

  &--theme-green {
    &::after,
    &::before {
      @apply bg-none bg-green;
    }

    @apply text-green;
  }

  &--theme-red {
    &::after,
    &::before {
      @apply bg-none bg-red;
    }

    @apply text-red;
  }

  &--theme-grey {
    &::after,
    &::before {
      @apply bg-none bg-grey-500;
    }

    @apply text-grey-500;
  }

  &--theme-white {
    &::after,
    &::before {
      @apply bg-none bg-white;
    }

    @apply text-white;
  }

  &--display-inline-block {
    @apply inline-block;

    @apply inline-block;
  }

  &--display-block {
    @apply block;

    @apply block;
  }
}

/* Fonts */
.VisionLink--mono-font {
  @apply font-fragment-mono text-xxs;
}

/* Promo Theme */
.VisionLink--promo-theme {
  @apply promo-t-gradient promo-t-gradient-animated;

  &::after,
  &::before {
    @apply promo-t-gradient-animated;
    background: linear-gradient(45deg,
    var(--promo-highlight, theme('colors.purple')) calc(var(--promo-gradient-percent) - 50%),
    var(--promo-highlight, theme('colors.blue')) calc(var(--promo-gradient-percent) - 45%),
    var(--promo-shine, theme('colors.blue-light')) var(--promo-gradient-percent),
    var(--promo-highlight, theme('colors.blue')) calc(var(--promo-gradient-percent) + 45%),
    var(--promo-highlight, theme('colors.purple')) calc(var(--promo-gradient-percent) + 50%));
  }
}
</style>
